import React, { useCallback, useEffect, useState } from 'react';
import { Divider, Grid, OutlinedInput, Typography } from '@mui/material';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import ChevronIcon from '../../../../icons/ChevronIcon';
import { FormattedMessage } from 'react-intl';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';

// Original class: dateDay
const StyledDateDay = styled(PickersDay)(theme => ({
  '&.MuiButtonBase-root': {
    '&:focus.Mui-selected': {
      backgroundColor: 'rgba(220, 223, 244, 1)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(220, 223, 244, 1)',
    },
  },
}));
// Original class: calendarResplanRoot
const StyledCalendarResplanRoot = styled(DateCalendar)(theme => ({
  '&.MuiCalendarPicker-root': {
    width: 'auto !important',
    '& .MuiSvgIcon-root': {
      width: 24,
      height: 24,
    },
  },

  '&.MuiMonthPicker-root': {
    width: 'auto !important',
  },

  '& .Mui-selected': {
    backgroundColor: '#dcdff4 !important',
    border: '1px solid #E4E4EF',
    borderRadius: '16px !important',
    color: '#7174AC !important',
  },

  '& .MuiPickersCalendarHeader-root': {
    display: 'none !important',
  },
}));
// Original class: filterIntInput
const StyledFilterIntInput = styled(OutlinedInput)(theme => ({
  maxWidth: 70,

  '&.MuiInputBase-root, &.MuiInputBase-root.Mui-focused': {
    '& input': {
      textAlign: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
    '& input[type="number"]': {
      '-moz-appearance': 'textfield',
    },
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
}));
// Original class: calendarRoot
const StyledCalendarRoot = styled(DateCalendar)(theme => ({
  '&.MuiCalendarPicker-root': {
    width: '100%',
    '& .MuiSvgIcon-root': {
      width: 24,
      height: 24,
    },
  },
}));
const FilterDateBetween = ({ showMonthPicker, filterOptions, dateParams, handleChooseSubItem, isClear }) => {
  const defaultValueName = filterOptions.chosenFirstStepItems[filterOptions.firstStepValues.value]
    ? filterOptions.firstStepValues.value
    : `${filterOptions.firstStepValues.value}_`;
  const [firstDateValue, setFirstDateValue] = useState(dayjs());
  const [secondDateValue, setSecondDateValue] = useState(dayjs());
  const [selectedMonth, setSelectedMonth] = useState(dayjs()); // Для нового блока
  const [calendarView, setCalendarView] = useState('month');

  useEffect(() => {
    if (isClear) {
      setFirstDateValue(null);
      setSecondDateValue(null);
      setSelectedMonth(null);
    }
    if (filterOptions.chosenFirstStepItems[defaultValueName]?.subLabels?.length > 0) {
      setFirstDateValue(dayjs(filterOptions.chosenFirstStepItems[defaultValueName]?.subLabels[0]?.valueId));
      setSecondDateValue(dayjs(filterOptions.chosenFirstStepItems[defaultValueName]?.subLabels[1]?.valueId));
      setSelectedMonth(dayjs(filterOptions.chosenFirstStepItems[defaultValueName]?.subLabels[0]?.valueId));
    }
  }, [defaultValueName, filterOptions.chosenFirstStepItems, isClear]);

  const handleChooseMonth = selectedDate => {
    if (selectedDate.isSame(selectedMonth, 'month')) {
      return;
    }
    let month, year;

    if (calendarView === 'month') {
      setSelectedMonth(selectedDate);
      month = selectedDate.month() + 1;
      year = selectedDate.year();
    } else {
      const newSelectedMonth = dayjs(selectedDate).set('month', selectedMonth.month());
      setSelectedMonth(newSelectedMonth);
      setCalendarView('month');
      month = newSelectedMonth.month() + 1;
      year = newSelectedMonth.year();
    }

    const dateOption = {
      month,
      year,
      valueName: dayjs(selectedDate).format('DD.MM.YYYY'),
      valueId: dayjs(selectedDate).format('YYYY-MM-DD'),
    };

    handleChooseSubItem({
      option: dateOption,
      isMultiple: false,
    });
  };

  const handleChooseFirstCalendarDay = useCallback(
    date => {
      const onDaySelect = selectedDate => {
        setFirstDateValue(dayjs(selectedDate).format() === dayjs(firstDateValue).format() ? dayjs() : selectedDate);
        setSecondDateValue(dayjs());
        handleChooseSubItem({
          option:
            dayjs(selectedDate).format() === dayjs(firstDateValue).format()
              ? { delete: true }
              : {
                  valueName: dayjs(selectedDate).format('DD.MM.YYYY'),
                  valueId: dayjs(selectedDate).format('YYYY-MM-DD'),
                },
          isMultiple: false,
        });
      };
      return (
        <StyledDateDay
          key={date}
          outsideCurrentMonth={false}
          day={date}
          disableRipple
          onDaySelect={onDaySelect}
          selected={dayjs(firstDateValue).format() === dayjs(date).format()}
        />
      );
    },
    [firstDateValue, handleChooseSubItem],
  );
  const handleChooseSecondCalendarDay = useCallback(
    date => {
      const onDaySelect = selectedDate => {
        setSecondDateValue(dayjs(selectedDate).format() === dayjs(secondDateValue).format() ? dayjs() : selectedDate);
        handleChooseSubItem({
          option:
            dayjs(selectedDate).format() === dayjs(secondDateValue).format()
              ? { delete: true }
              : {
                  valueName: dayjs(selectedDate).format('DD.MM.YYYY'),
                  valueId: dayjs(selectedDate).format('YYYY-MM-DD'),
                },
          isMultiple: false,
          type:
            filterOptions.chosenFirstStepItems[filterOptions.firstStepValues.value]?.subLabels.length === 1
              ? 'btw'
              : '',
          itemIndex: 1,
        });
      };
      return (
        <StyledDateDay
          key={date}
          outsideCurrentMonth={false}
          day={date}
          disableRipple
          onDaySelect={onDaySelect}
          selected={dayjs(secondDateValue).format() === dayjs(date).format()}
        />
      );
    },
    [filterOptions.chosenFirstStepItems, filterOptions.firstStepValues.value, handleChooseSubItem, secondDateValue],
  );

  return (
    <Grid container direction="row" wrap="nowrap">
      {showMonthPicker ? (
        <StyledCalendarResplanRoot
          dayOfWeekFormatter={dateParams.formatter}
          value={selectedMonth}
          view={calendarView}
          onChange={handleChooseMonth}
          slots={{
            switchViewIcon: () => null,
          }}
        />
      ) : (
        <>
          {filterOptions.chosenFirstStepItems[defaultValueName]?.notParsedType.includes('>') ||
          filterOptions.chosenFirstStepItems[defaultValueName]?.notParsedType.includes('<') ||
          filterOptions.chosenFirstStepItems[defaultValueName]?.notParsedType.includes('-') ? (
            <>
              <Grid
                container
                wrap="nowrap"
                sx={{
                  alignItems: 'center',
                }}
              >
                <StyledFilterIntInput
                  type="number"
                  placeholder="0"
                  value={
                    filterOptions.chosenFirstStepItems[filterOptions.firstStepValues.value]?.subLabels[0]?.value || ''
                  }
                  onChange={e =>
                    handleChooseSubItem({
                      option: {
                        valueName:
                          e.target.value.length === 0
                            ? e.target.value
                            : dayjs().subtract(Number(e.target.value), 'day').format('DD.MM.YYYY'),
                        valueId:
                          e.target.value.length === 0
                            ? e.target.value
                            : dayjs().subtract(Number(e.target.value), 'day').format('YYYY-MM-DD'),
                        value: e.target.value,
                      },
                      isMultiple: false,
                    })
                  }
                />
                <Typography
                  variant="h5"
                  sx={{
                    textTransform: 'lowercase',
                  }}
                >
                  <FormattedMessage id="days" />
                </Typography>
              </Grid>
            </>
          ) : (
            <StyledCalendarRoot
              dayOfWeekFormatter={dateParams.formatter}
              value={firstDateValue}
              renderDay={handleChooseFirstCalendarDay}
              showDaysOutsideCurrentMonth
              view="day"
              slots={{
                switchViewIcon: () => null,
              }}
              onChange={e => e.preventDefault()}
            />
          )}
          <Divider orientation="vertical" flexItem />
          {filterOptions.chosenFirstStepItems[filterOptions.firstStepValues.value]?.type === 'btw' && (
            <>
              <Divider orientation="vertical" flexItem />
              <StyledCalendarRoot
                dayOfWeekFormatter={dateParams.formatter}
                readOnly={true}
                value={secondDateValue}
                renderDay={handleChooseSecondCalendarDay}
                showDaysOutsideCurrentMonth
                view="day"
                slots={{
                  switchViewIcon: () => null,
                }}
                onChange={e => e.preventDefault()}
              />
            </>
          )}
        </>
      )}
    </Grid>
  );
};

export default FilterDateBetween;
